import DateInput from '../../components/DateInput';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Plus, Trash2, ClipboardList, FileText, Edit3, Save, AlertTriangle, AlertCircle, Sparkles, RefreshCw, ShieldAlert, CalendarClock, Brain, X } from 'lucide-react';
import PatientChartQuickLinks from '../../components/PatientChartQuickLinks';
import PostureSnapshotTimeline from '../../components/PostureSnapshotTimeline';
import { printHtml } from '../../lib/print';
import { safeConfirm } from '../../lib/confirm';

export default function PatientChart() {
  const { patient_id } = useParams();
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [plan, setPlan] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    patient_id, visit_date: new Date().toISOString().slice(0,10),
    subjective: '', objective: '', assessment: '', plan: '',
    therapies_performed: '', outcomes: '', therapist_name: ''
  });
  const [editId, setEditId] = useState(null);
  const [dischargeForm, setDischargeForm] = useState({ discharged: false, discharge_date: '', discharge_notes: '' });
  const [savingDis, setSavingDis] = useState(false);

  const [printOpen, setPrintOpen] = useState(false);
  const [printSel, setPrintSel] = useState({
    visits: true, discharge: true, referral: false,
    emergency: false, interview: false, treatment: false, objective: false,
  });
  const [interview, setInterview] = useState(null);
  const [objective, setObjective] = useState(null);
  const [upcomingAppt, setUpcomingAppt] = useState(null);
  const [wizardContext, setWizardContext] = useState(null);
  const [settings, setSettings] = useState({});
  // Iter 100 — AI Differential Diagnosis state
  const [ddxOpen, setDdxOpen] = useState(false);
  const [ddxBusy, setDdxBusy] = useState(false);
  const [ddxResult, setDdxResult] = useState(null);
  const [ddxError, setDdxError] = useState('');
  const [ddxNotes, setDdxNotes] = useState('');

  const runDifferentialDx = async () => {
    setDdxBusy(true); setDdxError(''); setDdxResult(null);
    try {
      const { data } = await api.post('/ai/differential-dx', {
        patient_id, extra_notes: ddxNotes || undefined,
      });
      setDdxResult(data);
    } catch (e) {
      setDdxError(e?.response?.data?.detail || e.message);
    } finally { setDdxBusy(false); }
  };

  const load = () => api.get(`/visits/${patient_id}`).then(r => setVisits(r.data));
  const loadInterview = () => api.get(`/interviews/${patient_id}`).then(r => setInterview(r.data)).catch(() => {});
  useEffect(() => {
    api.get(`/patients/${patient_id}`).then(r => {
      setPatient(r.data);
      setDischargeForm({
        discharged: !!r.data.discharged,
        discharge_date: r.data.discharge_date || '',
        discharge_notes: r.data.discharge_notes || '',
      });
    });
    load();
    loadInterview();
    api.get(`/treatment-plans/${patient_id}`).then(r => setPlan(r.data));
    // Find the patient's next upcoming appointment (scheduled or pending) so the
    // clinician sees the patient-reported reason before they start the session.
    api.get('/appointments').then(r => {
      const now = Date.now();
      const upcoming = (r.data || [])
        .filter(a => a.patient_id === patient_id
                  && ['scheduled', 'pending_approval'].includes(a.status)
                  && new Date(a.starts_at).getTime() >= now - 3600_000)
        .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
      setUpcomingAppt(upcoming[0] || null);
    }).catch(() => {});
    // Wizard context (3-step landing wizard answers from the patient's first visit).
    api.get(`/wizard/context/${patient_id}`).then(r => setWizardContext(r.data && Object.keys(r.data).length ? r.data : null)).catch(()=>{});
    api.get('/clinic/settings').then(r => setSettings(r.data || {})).catch(()=>{});
  }, [patient_id]);

  const [rescreening, setRescreening] = useState(false);
  const rescreen = async () => {
    setRescreening(true);
    try {
      await api.post(`/interviews/${patient_id}/rescreen`);
      await loadInterview();
    } catch (e) {
      alert(e.response?.data?.detail || 'AI re-screen unavailable');
    } finally { setRescreening(false); }
  };

  const submit = async () => {
    if (editId) {
      await api.put(`/visits/${editId}`, form);
    } else {
      await api.post('/visits', form);
    }
    setShow(false); setEditId(null);
    setForm({ ...form, subjective:'', objective:'', assessment:'', plan:'', therapies_performed:'', outcomes:'' });
    load();
  };

  const startEdit = (v) => {
    setEditId(v.visit_id);
    setForm({
      patient_id, visit_date: v.visit_date,
      subjective: v.subjective || '', objective: v.objective || '',
      assessment: v.assessment || '', plan: v.plan || '',
      therapies_performed: v.therapies_performed || '', outcomes: v.outcomes || '',
      therapist_name: v.therapist_name || ''
    });
    setShow(true);
  };

  const remove = async (id) => {
    if (!await safeConfirm('Delete this visit entry?')) return;
    await api.delete(`/visits/${id}`);
    load();
  };

  // Builds a discharge summary pre-filled from the chart (iter 144).
  const buildDischargeSummary = () => {
    const lines = [];
    lines.push(`Patient: ${patient?.full_name || ''}${patient?.date_of_birth ? ` (DOB ${patient.date_of_birth})` : ''}`);
    if (interview?.chief_complaint) lines.push(`Presenting complaint: ${interview.chief_complaint}`);
    if (visits.length) {
      const first = visits[visits.length - 1]?.visit_date || '';
      const last = visits[0]?.visit_date || '';
      lines.push(`Completed visits: ${visits.length}${first ? ` (first ${first}, last ${last})` : ''}`);
      if (visits[0]?.plan) lines.push(`Last plan: ${visits[0].plan}`);
      if (visits[0]?.outcomes) lines.push(`Last outcome: ${visits[0].outcomes}`);
    }
    lines.push('Discharge reason: treatment goals met / plan completed.');
    return lines.join('\n');
  };

  const saveDischarge = async () => {
    setSavingDis(true);
    try {
      const r = await api.put(`/patients/${patient_id}/discharge`, dischargeForm);
      setPatient(p => ({ ...(p || {}), ...r.data }));
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSavingDis(false); }
  };

  const [briefingLoading, setBriefingLoading] = useState(false);
  const openBriefing = async () => {
    setBriefingLoading(true);
    try {
      const { data } = await api.get(`/briefing/${patient_id}`);
      const html = renderBriefingHtml(data);
      printHtml(html, `Pre-visit briefing — ${data.patient?.full_name || ''}`);
    } catch (e) {
      alert('Failed to load briefing: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBriefingLoading(false);
    }
  };

  const doPrint = async () => {
    // Lazy-load any sections we need for printing
    let intv = interview, obj = objective;
    if (printSel.interview && !intv) {
      const r = await api.get(`/interviews/${patient_id}`).catch(() => null);
      intv = r?.data || {}; setInterview(intv);
    }
    if (printSel.objective && !obj) {
      const r = await api.get(`/objective/${patient_id}`).catch(() => null);
      obj = r?.data || {}; setObjective(obj);
    }

    const head = `
      <div class="row">
        <div>
          <h2>Patient chart</h2>
          <h1>${escapeHtml(patient?.full_name || '')}</h1>
          <div class="muted">${patient?.country || ''} ${patient?.date_of_birth ? '· DOB ' + patient.date_of_birth : ''}
          ${patient?.discharged ? '· DISCHARGED ' + (patient?.discharge_date || '') : ''}</div>
        </div>
        <div style="text-align:right;">
          <div class="clinic">Physiogenic</div>
          <div class="muted">Generated ${new Date().toLocaleString()}</div>
        </div>
      </div>`;

    const sections = [];
    if (printSel.visits) {
      sections.push(`<h3>Visit timeline</h3>` + (visits.length ? `
        <table><thead><tr><th>Date</th><th>S</th><th>O</th><th>A</th><th>P</th><th>Therapies</th><th>Outcomes</th></tr></thead>
        <tbody>${visits.map(v => `<tr>
          <td>${v.visit_date || ''}<br><span class="muted">${escapeHtml(v.therapist_name || '')}</span></td>
          <td>${escapeHtml(v.subjective || '')}</td>
          <td>${escapeHtml(v.objective || '')}</td>
          <td>${escapeHtml(v.assessment || '')}</td>
          <td>${escapeHtml(v.plan || '')}</td>
          <td>${escapeHtml(v.therapies_performed || '')}</td>
          <td>${escapeHtml(v.outcomes || '')}</td>
        </tr>`).join('')}</tbody></table>
      ` : '<div class="muted">No visits.</div>'));
    }
    if (printSel.discharge && dischargeForm.discharged) {
      sections.push(`
        <h3>Discharge</h3>
        <div><strong>Date:</strong> ${dischargeForm.discharge_date || '—'}</div>
        ${dischargeForm.discharge_notes ? `<div style="margin-top:6px;">${escapeHtml(dischargeForm.discharge_notes)}</div>` : ''}`);
    }
    if (printSel.referral) {
      sections.push(`
        <h3>Referral</h3>
        <div><strong>Source:</strong> ${escapeHtml(patient?.referral_source || '—')}</div>
        <div><strong>Physician:</strong> ${escapeHtml(patient?.referral_physician || '—')}</div>
        ${patient?.referral_notes ? `<div><strong>Notes:</strong> ${escapeHtml(patient.referral_notes)}</div>` : ''}`);
    }
    if (printSel.emergency) {
      sections.push(`
        <h3>Emergency contact</h3>
        <div><strong>Name:</strong> ${escapeHtml(patient?.emergency_contact_name || '—')}</div>
        <div><strong>Phone:</strong> ${escapeHtml(patient?.emergency_contact_phone || '—')}</div>
        <div><strong>Relation:</strong> ${escapeHtml(patient?.emergency_contact_relation || '—')}</div>`);
    }
    if (printSel.interview && intv?.chief_complaint) {
      const yn = (v) => v === true ? 'Yes' : v === false ? 'No' : '—';
      const yes = (v) => v === true;
      sections.push(`
        <h3>Subjective interview</h3>
        <div><strong>Chief complaint:</strong> ${escapeHtml(intv.chief_complaint || '')}</div>
        <div><strong>VAS pain:</strong> ${intv.pain_score ?? '—'}/10 · <strong>Onset:</strong> ${escapeHtml(intv.onset || '—')}</div>
        ${intv.diagnosis ? `<div><strong>Diagnosis:</strong> ${escapeHtml(intv.diagnosis)}</div>` : ''}
        ${intv.icd10_codes?.length ? `<div><strong>ICD-10 (diagnosis):</strong> ${intv.icd10_codes.join(', ')}</div>` : ''}
        ${intv.icd9_codes?.length ? `<div><strong>ICD-9 (procedures):</strong> ${intv.icd9_codes.join(', ')}</div>` : ''}
        ${intv.icf_codes?.length ? `<div><strong>ICF (functioning):</strong> ${intv.icf_codes.join(', ')}</div>` : ''}
        ${intv.diseases?.length ? `<div><strong>Comorbidities:</strong> ${intv.diseases.join(', ')}</div>` : ''}
        ${intv.goals ? `<div><strong>Goals:</strong> ${escapeHtml(intv.goals)}</div>` : ''}
        ${intv.therapist_notes ? `<div><strong>Therapist notes:</strong> ${escapeHtml(intv.therapist_notes)}</div>` : ''}
        ${(intv.pmh_conditions?.length || intv.pmh_other) ? `
          <h4 style="margin-top:14px;font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#003D80;">Past Medical History</h4>
          ${intv.pmh_conditions?.length ? `<ul style="columns:2;margin:6px 0 6px 16px;">${intv.pmh_conditions.map(c => `<li>${escapeHtml(c)}</li>`).join('')}</ul>` : ''}
          ${intv.pmh_other ? `<div><strong>Other:</strong> ${escapeHtml(intv.pmh_other)}</div>` : ''}
        ` : ''}
        <h4 style="margin-top:14px;font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#003D80;">Risk screening</h4>
        <div><strong>Falls (past year):</strong> ${yn(intv.falls_last_year)}${yes(intv.falls_last_year) && intv.falls_count ? ` — ${escapeHtml(String(intv.falls_count))}` : ''}</div>
        <div><strong>Fractures history:</strong> ${yn(intv.fractures_history)}${yes(intv.fractures_history) && intv.fractures_where ? ` — ${escapeHtml(intv.fractures_where)}` : ''}</div>
        <div><strong>Metal implants:</strong> ${yn(intv.metal_implants)}${yes(intv.metal_implants) && intv.metal_implants_where ? ` — ${escapeHtml(intv.metal_implants_where)}` : ''}</div>
        <div><strong>Smoker:</strong> ${yn(intv.smoker)}${yes(intv.smoker) && intv.smoking_per_day ? ` — ${escapeHtml(intv.smoking_per_day)}/day` : ''}</div>
        <div><strong>Exercises regularly:</strong> ${yn(intv.exercises_regularly)}${yes(intv.exercises_regularly) && intv.exercise_frequency ? ` — ${escapeHtml(intv.exercise_frequency)}` : ''}</div>
        <div><strong>Allergies:</strong> ${yn(intv.has_allergies)}${yes(intv.has_allergies) && intv.allergies_list ? ` — ${escapeHtml(intv.allergies_list)}` : ''}</div>
        <div><strong>Pregnant:</strong> ${yn(intv.is_pregnant)}</div>
        ${intv.medications ? `<div style="margin-top:8px;"><strong>Medications:</strong><br>${escapeHtml(intv.medications).replace(/\\n/g, '<br>')}</div>` : ''}
        ${intv.surgical_history ? `<div style="margin-top:6px;"><strong>Surgeries:</strong><br>${escapeHtml(intv.surgical_history).replace(/\\n/g, '<br>')}</div>` : ''}
        ${intv.diagnostic_tests?.length ? `<div style="margin-top:6px;"><strong>Diagnostic tests:</strong> ${intv.diagnostic_tests.join(', ')}</div>` : ''}
        ${intv.ai_screen?.flags?.length ? `
          <h4 style="margin-top:14px;font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#B83A3A;">AI red-flag screen — ${intv.ai_screen.flags.length} caution(s)</h4>
          <ul style="margin:4px 0 6px 16px;">${intv.ai_screen.flags.map(f => `<li><strong>[${f.severity.toUpperCase()}] ${escapeHtml(f.tag)}:</strong> ${escapeHtml(f.why)}</li>`).join('')}</ul>
        ` : ''}`);
    }
    if (printSel.treatment && plan?.diagnosis) {
      sections.push(`
        <h3>Treatment plan</h3>
        <div><strong>Diagnosis:</strong> ${escapeHtml(plan.diagnosis || '')}</div>
        ${plan.short_term_goals ? `<div><strong>Short-term goals:</strong> ${escapeHtml(plan.short_term_goals)}</div>` : ''}
        ${plan.long_term_goals ? `<div><strong>Long-term goals:</strong> ${escapeHtml(plan.long_term_goals)}</div>` : ''}
        ${plan.modalities ? `<div><strong>Modalities:</strong> ${escapeHtml(plan.modalities)}</div>` : ''}
        ${plan.frequency ? `<div><strong>Frequency:</strong> ${escapeHtml(plan.frequency)}</div>` : ''}
        ${plan.signed ? `<div class="muted">Signed by patient.</div>` : ''}`);
    }
    if (printSel.objective && obj && (obj.muscle_tests?.length || obj.goniometer?.length || obj.neurology?.length || obj.balance?.length || obj.vitals || obj.cardiovascular)) {
      const v = obj.vitals || {};
      sections.push(`
        <h3>Objective assessment</h3>
        <div><strong>Vitals:</strong> BP ${v.bp || '—'} · HR ${v.hr || '—'} · RR ${v.rr || '—'} · SpO₂ ${v.spo2 || '—'} · Temp ${v.temp || '—'}</div>
        ${(obj.muscle_tests || []).length ? `<h4 style="margin:8px 0 4px;">MMT</h4><table><thead><tr><th>Muscle</th><th>Side</th><th>Grade</th><th>Notes</th></tr></thead><tbody>${obj.muscle_tests.map(r => `<tr><td>${escapeHtml(r.muscle || '')}</td><td>${r.side || ''}</td><td>${r.grade || ''}</td><td>${escapeHtml(r.notes || '')}</td></tr>`).join('')}</tbody></table>` : ''}
        ${(obj.goniometer || []).length ? `<h4 style="margin:8px 0 4px;">Goniometer</h4><table><thead><tr><th>Joint</th><th>Motion</th><th>Side</th><th>aROM</th><th>pROM</th><th>Notes</th></tr></thead><tbody>${obj.goniometer.map(r => `<tr><td>${escapeHtml(r.joint || '')}</td><td>${escapeHtml(r.motion || '')}</td><td>${r.side || ''}</td><td>${r.aROM || ''}</td><td>${r.pROM || ''}</td><td>${escapeHtml(r.notes || '')}</td></tr>`).join('')}</tbody></table>` : ''}
        ${(obj.neurology || []).length ? `<h4 style="margin:8px 0 4px;">Neurology</h4><table><thead><tr><th>Test</th><th>Side</th><th>Result</th><th>Notes</th></tr></thead><tbody>${obj.neurology.map(r => `<tr><td>${escapeHtml(r.test || '')}</td><td>${r.side || ''}</td><td>${escapeHtml(r.result || '')}</td><td>${escapeHtml(r.notes || '')}</td></tr>`).join('')}</tbody></table>` : ''}
        ${(obj.balance || []).length ? `<h4 style="margin:8px 0 4px;">Balance</h4><table><thead><tr><th>Test</th><th>Result</th><th>Notes</th></tr></thead><tbody>${obj.balance.map(r => `<tr><td>${escapeHtml(r.test || '')}</td><td>${escapeHtml(r.result || '')}</td><td>${escapeHtml(r.notes || '')}</td></tr>`).join('')}</tbody></table>` : ''}`);
    }

    const html = head + sections.join('<hr style="border:none;border-top:1px solid #E8DFC8;margin:18px 0;"/>') +
      '<div class="footer">Physiogenic · HIPAA & GDPR compliant clinical record</div>';
    printHtml(html, `Chart - ${patient?.full_name || patient_id}`);
    setPrintOpen(false);
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6 print:mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">MEDICAL RECORD</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Patient chart</h1>
          {patient && (
            <p className="text-fg-muted mt-2">
              {patient.full_name} · {patient.country} {patient.date_of_birth ? `· DOB ${patient.date_of_birth}` : ''}
              {patient.discharged && <span className="ml-2 badge-neutral">Discharged {patient.discharge_date || ''}</span>}
            </p>
          )}
        </div>
        <PatientChartQuickLinks
          patient_id={patient_id}
          settings={settings}
          printOpen={printOpen} setPrintOpen={setPrintOpen}
          printSel={printSel} setPrintSel={setPrintSel} doPrint={doPrint}
          openBriefing={openBriefing} briefingLoading={briefingLoading}
          onNewVisit={() => { setEditId(null); setShow(s => !s); }}
        />
      </div>

      {/* Iter 100 — AI Differential Diagnosis launcher */}
      <div className="mb-5 surface border border-soft rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="ddx-launcher">
        <div className="flex items-center gap-2.5">
          <Brain className="w-5 h-5 text-brand"/>
          <div>
            <div className="font-heading font-semibold text-sm">AI clinical reasoning</div>
            <p className="text-xs text-fg-muted">Generate a ranked top-5 differential diagnosis from this chart.</p>
          </div>
        </div>
        <button onClick={() => setDdxOpen(true)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="ddx-open-btn">
          <Sparkles className="w-4 h-4"/> Run differential
        </button>
      </div>

      <AIScreenBanner screen={interview?.ai_screen} onRescreen={rescreen} rescreening={rescreening}/>

      {/* Wizard-context banner — shown when the patient answered the public
          3-step landing wizard. Gives the clinician prep context BEFORE the
          first formal intake is completed. */}
      {wizardContext && (
        <div className="mb-5 rounded-xl p-4 border border-accent/30 bg-accent/5" data-testid="wizard-context-banner">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 mt-0.5 shrink-0 text-accent"/>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">PRE-VISIT CONTEXT (FROM BOOKING WIZARD)</div>
              <div className="text-sm text-fg space-y-1">
                {wizardContext.complaint && (
                  <div><span className="text-fg-muted text-xs uppercase mr-2">Concern:</span><span className="italic">"{wizardContext.complaint}"</span></div>
                )}
                {wizardContext.discipline && wizardContext.discipline !== 'unsure' && (
                  <div><span className="text-fg-muted text-xs uppercase mr-2">Service:</span><span className="capitalize">{wizardContext.discipline}</span></div>
                )}
                {wizardContext.timeline && (
                  <div><span className="text-fg-muted text-xs uppercase mr-2">Timeline:</span>{({this_week:'This week', soon:'Next 2–4 weeks', flexible:'Whenever'})[wizardContext.timeline] || wizardContext.timeline}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {upcomingAppt && (upcomingAppt.reason_for_visit || upcomingAppt.new_condition || upcomingAppt.is_first_visit) && (
        <div className={`mb-5 rounded-xl p-4 border ${upcomingAppt.new_condition || upcomingAppt.is_first_visit
                                                       ? 'bg-[#FFF4D6] border-[#FFB000]/45'
                                                       : 'bg-brand/5 border-brand/20'}`}
             data-testid="upcoming-visit-banner">
          <div className="flex items-start gap-3">
            <CalendarClock className={`w-5 h-5 mt-0.5 shrink-0 ${upcomingAppt.new_condition || upcomingAppt.is_first_visit ? 'text-[#8A5A00]' : 'text-brand'}`}/>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className={`text-sm font-medium ${upcomingAppt.new_condition || upcomingAppt.is_first_visit ? 'text-[#8A5A00]' : 'text-brand'}`}>
                  Upcoming visit · {new Date(upcomingAppt.starts_at).toLocaleString()}
                </div>
                {upcomingAppt.is_first_visit && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brand/15 text-brand">FIRST VISIT</span>
                )}
                {upcomingAppt.new_condition && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#FFB000] text-[#5A3A00]" data-testid="upcoming-new-cond">
                    <AlertCircle className="w-3 h-3"/> NEW CONDITION
                  </span>
                )}
                {upcomingAppt.status === 'pending_approval' && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#FFB000]/30 text-[#8A5A00]">PENDING APPROVAL</span>
                )}
              </div>
              {upcomingAppt.reason_for_visit ? (
                <div className="text-sm mt-1.5 italic text-fg-secondary" data-testid="upcoming-reason">
                  "{upcomingAppt.reason_for_visit}"
                </div>
              ) : (
                <div className="text-xs text-fg-muted mt-1 italic">No patient-stated reason provided.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <PostureSnapshotTimeline patientId={patient_id}/>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="md:col-span-2 admin-card">
          <h3 className="font-heading font-semibold mb-3">Visit timeline</h3>
          {show && (
            <div className="surface border border-soft rounded-lg p-4 mb-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Visit date"><DateInput value={form.visit_date} onChange={e=>setForm({...form, visit_date: e.target.value})} data-testid="v-date"/></Field>
                <Field label="Therapist"><input className="input" value={form.therapist_name} onChange={e=>setForm({...form, therapist_name: e.target.value})}/></Field>
                <Field label="S — Subjective" full><textarea className="input" value={form.subjective} onChange={e=>setForm({...form, subjective: e.target.value})} data-testid="v-subjective"/></Field>
                <Field label="O — Objective findings" full><textarea className="input" value={form.objective} onChange={e=>setForm({...form, objective: e.target.value})} data-testid="v-objective"/></Field>
                <Field label="A — Assessment" full><textarea className="input" value={form.assessment} onChange={e=>setForm({...form, assessment: e.target.value})}/></Field>
                <Field label="P — Plan" full><textarea className="input" value={form.plan} onChange={e=>setForm({...form, plan: e.target.value})}/></Field>
                <Field label="Therapies performed" full><input className="input" value={form.therapies_performed} onChange={e=>setForm({...form, therapies_performed: e.target.value})}/></Field>
                <Field label="Outcomes / response" full><input className="input" value={form.outcomes} onChange={e=>setForm({...form, outcomes: e.target.value})}/></Field>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={()=>{setShow(false); setEditId(null);}} className="btn-secondary">Cancel</button>
                <button onClick={submit} className="btn-primary" data-testid="v-save">{editId?'Update':'Add'} visit</button>
              </div>
            </div>
          )}
          {visits.map(v => (
            <div key={v.visit_id} className="surface border border-soft rounded-lg p-4 mb-3" data-testid={`visit-${v.visit_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-heading font-semibold flex items-center gap-2">
                  {v.visit_number && (
                    <span className="text-[10px] uppercase tracking-wider bg-brand/10 text-brand px-2 py-0.5 rounded-full font-mono" data-testid={`v-num-${v.visit_id}`}>
                      #{String(v.visit_number).padStart(4, '0')}
                    </span>
                  )}
                  {v.record_no && (
                    <span className="text-[10px] uppercase tracking-wider text-fg-muted font-mono">{v.record_no}</span>
                  )}
                  {v.visit_date} {v.therapist_name && <span className="text-fg-muted font-normal">· {v.therapist_name}</span>}
                </div>
                <div className="flex gap-1 print:hidden">
                  <button onClick={()=>startEdit(v)} className="text-brand p-1" data-testid={`v-edit-${v.visit_id}`}><Edit3 className="w-4 h-4"/></button>
                  <button onClick={()=>remove(v.visit_id)} className="text-[#B83A3A] p-1" data-testid={`v-del-${v.visit_id}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
              <SoapRow label="S" v={v.subjective} />
              <SoapRow label="O" v={v.objective} />
              <SoapRow label="A" v={v.assessment} />
              <SoapRow label="P" v={v.plan} />
              {v.therapies_performed && <div className="text-xs text-fg-muted mt-2"><strong>Therapies:</strong> {v.therapies_performed}</div>}
              {v.outcomes && <div className="text-xs text-fg-muted"><strong>Outcomes:</strong> {v.outcomes}</div>}
            </div>
          ))}
          {!visits.length && !show && <div className="text-fg-muted text-sm">No visits recorded yet.</div>}
        </div>

        <div className="admin-card">
          <h3 className="font-heading font-semibold mb-3">Discharge</h3>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={!!dischargeForm.discharged}
                    onChange={e=>{
                      const on = e.target.checked;
                      if (on) {
                        // Auto-populate required fields from the patient's data.
                        setDischargeForm(f => ({
                          discharged: true,
                          discharge_date: f.discharge_date || new Date().toISOString().slice(0, 10),
                          discharge_notes: f.discharge_notes || buildDischargeSummary(),
                        }));
                      } else {
                        setDischargeForm(f => ({ ...f, discharged: false }));
                      }
                    }}
                    data-testid="discharge-toggle"/>
            Patient discharged
          </label>
          <Field label="Discharge date">
            <DateInput value={dischargeForm.discharge_date}
                    onChange={e=>setDischargeForm({...dischargeForm, discharge_date: e.target.value})}
                    disabled={!dischargeForm.discharged} data-testid="discharge-date"/>
          </Field>
          <Field label="Discharge notes">
            <textarea className="input min-h-[100px]" value={dischargeForm.discharge_notes}
                      onChange={e=>setDischargeForm({...dischargeForm, discharge_notes: e.target.value})}
                      disabled={!dischargeForm.discharged} data-testid="discharge-notes"/>
          </Field>
          <button onClick={saveDischarge} disabled={savingDis} className="btn-primary w-full inline-flex items-center justify-center gap-2 print:hidden" data-testid="discharge-save">
            <Save className="w-4 h-4"/> Save
          </button>

          <div className="mt-6 pt-4 border-t border-soft">
            <h4 className="font-heading font-semibold text-sm mb-2">Referral</h4>
            {patient?.referral_physician || patient?.referral_source ? (
              <div className="text-xs text-fg-secondary space-y-1">
                <div><strong>Source:</strong> {patient.referral_source || '—'}</div>
                <div><strong>Physician:</strong> {patient.referral_physician || '—'}</div>
                {patient.referral_notes && <div><strong>Notes:</strong> {patient.referral_notes}</div>}
              </div>
            ) : <div className="text-xs text-fg-muted">No referral info.</div>}
          </div>

          <div className="mt-6 pt-4 border-t border-soft">
            <h4 className="font-heading font-semibold text-sm mb-2">Emergency contact</h4>
            {patient?.emergency_contact_name ? (
              <div className="text-xs text-fg-secondary space-y-1">
                <div><strong>Name:</strong> {patient.emergency_contact_name}</div>
                <div><strong>Phone:</strong> {patient.emergency_contact_phone || '—'}</div>
                <div><strong>Relation:</strong> {patient.emergency_contact_relation || '—'}</div>
              </div>
            ) : <div className="text-xs text-fg-muted">Not provided.</div>}
          </div>
        </div>
      </div>

      {/* Iter 100 — AI Differential Diagnosis modal */}
      {ddxOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDdxOpen(false)} data-testid="ddx-modal">
          <div className="bg-bg-secondary rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-brand"/>
                <h2 className="font-heading font-bold text-lg">AI differential diagnosis</h2>
              </div>
              <button onClick={() => setDdxOpen(false)}><X className="w-5 h-5"/></button>
            </div>
            <p className="text-xs text-fg-muted mb-4">
              Claude Sonnet 4.5 reads this patient's chief complaint + objective findings + history + last 5 SOAP notes and proposes the top-5 differentials. <strong>Always confirm with your own clinical judgement.</strong> Audited.
            </p>
            {!ddxResult ? (
              <>
                <label className="block mb-3">
                  <span className="text-xs font-medium">Extra clinician notes (optional)</span>
                  <textarea className="input mt-1 w-full" rows={2}
                            value={ddxNotes} onChange={e => setDdxNotes(e.target.value)}
                            placeholder="e.g. patient is post-COVID, suspect deconditioning vs primary mechanical"
                            data-testid="ddx-extra-notes"/>
                </label>
                {ddxError && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3" data-testid="ddx-error">{ddxError}</div>}
                <button onClick={runDifferentialDx} disabled={ddxBusy}
                        className="btn-primary inline-flex items-center gap-2 w-full justify-center"
                        data-testid="ddx-run-btn">
                  <Sparkles className="w-4 h-4"/> {ddxBusy ? 'Reasoning…' : 'Generate differential'}
                </button>
              </>
            ) : (
              <>
                {ddxResult.summary && (
                  <div className="bg-brand/5 border border-brand/20 rounded p-3 text-sm mb-4" data-testid="ddx-summary">
                    <strong>Impression:</strong> {ddxResult.summary}
                  </div>
                )}
                <ol className="space-y-3" data-testid="ddx-items">
                  {(ddxResult.items || []).map((it, i) => (
                    <li key={i} className="border border-soft rounded-lg p-3" data-testid={`ddx-item-${i}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold">{i + 1}. {it.dx}</div>
                        <div className="text-xs bg-brand/15 text-brand px-2 py-0.5 rounded font-semibold">
                          {Math.round((it.probability || 0) * 100)}%
                        </div>
                      </div>
                      {it.icd10 && <div className="text-[10px] text-fg-muted mb-1">ICD-10: <span className="font-mono">{it.icd10}</span></div>}
                      <div className="text-sm mt-1"><strong>Rationale:</strong> {it.rationale}</div>
                      <div className="text-sm mt-1"><strong>Rule out:</strong> {it.rule_out}</div>
                      {it.red_flags?.length > 0 && (
                        <div className="mt-2 text-xs bg-red-50 border border-red-200 rounded p-2 text-red-800" data-testid={`ddx-redflags-${i}`}>
                          <strong>⚠ Red flags:</strong> {it.red_flags.join('; ')}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
                <div className="flex justify-between items-center mt-4">
                  <button onClick={() => { setDdxResult(null); setDdxError(''); }}
                          className="text-xs text-brand hover:underline">← Run again with different notes</button>
                  <button onClick={() => setDdxOpen(false)} className="btn-secondary text-sm">Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={full ? 'md:col-span-2 block' : 'block'}>
      <div className="text-xs text-fg-muted mb-1 mt-2">{label}</div>
      {children}
    </label>
  );
}

function SoapRow({ label, v }) {
  if (!v) return null;
  return (
    <div className="text-xs mt-1 flex gap-2">
      <span className="font-mono font-bold text-brand min-w-[16px]">{label}</span>
      <span className="text-fg-secondary">{v}</span>
    </div>
  );
}

function AIScreenBanner({ screen, onRescreen, rescreening }) {
  if (!screen) return null;
  const flags = screen.flags || [];
  const high = flags.filter(f => f.severity === 'high');
  const medium = flags.filter(f => f.severity === 'medium');
  if (!flags.length) {
    return (
      <div className="mb-5 surface border border-[#3A664D]/30 rounded-xl p-4 flex items-start gap-3" data-testid="ai-screen-banner-clear">
        <Sparkles className="w-5 h-5 text-[#3A664D] mt-0.5 shrink-0"/>
        <div className="flex-1">
          <div className="text-sm font-medium text-[#3A664D]">AI intake screen — no red flags</div>
          <div className="text-xs text-fg-muted mt-0.5">Claude reviewed the patient's PMH, medications, and complaint history. No contraindications identified. Always cross-check with your own clinical judgment.</div>
        </div>
        <button onClick={onRescreen} disabled={rescreening}
                className="text-xs text-fg-muted hover:text-brand inline-flex items-center gap-1 shrink-0"
                data-testid="rescreen-btn">
          <RefreshCw className={`w-3 h-3 ${rescreening ? 'animate-spin' : ''}`}/> Re-screen
        </button>
      </div>
    );
  }
  const tone = high.length ? 'high' : medium.length ? 'medium' : 'low';
  const palette = {
    high:   { bg: 'bg-[#FBE9E5]', border: 'border-[#B83A3A]/35', text: 'text-[#B83A3A]', icon: ShieldAlert },
    medium: { bg: 'bg-[#FFF4D6]', border: 'border-[#FFB000]/45', text: 'text-[#8A5A00]', icon: AlertTriangle },
    low:    { bg: 'bg-brand/5',   border: 'border-brand/25',     text: 'text-brand',     icon: AlertTriangle },
  }[tone];
  const Icon = palette.icon;
  return (
    <div className={`mb-5 rounded-xl p-4 border ${palette.bg} ${palette.border}`} data-testid={`ai-screen-banner-${tone}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${palette.text}`}/>
        <div className="flex-1">
          <div className={`text-sm font-medium ${palette.text}`}>
            AI intake screen — {flags.length} caution{flags.length === 1 ? '' : 's'} flagged
            {high.length > 0 && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold">{high.length} HIGH</span>}
          </div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {flags.map((f, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`flag-${i}`}>
                <SeverityChip sev={f.severity}/>
                <div>
                  <span className="font-medium text-fg">{f.tag}</span>
                  <span className="text-fg-secondary"> · {f.why}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[10px] text-fg-muted italic">Generated by Claude {screen.model?.split('-').slice(0,3).join('-') || 'AI'} — informational only, not a substitute for clinical judgment.</div>
        </div>
        <button onClick={onRescreen} disabled={rescreening}
                className="text-xs text-fg-muted hover:text-brand inline-flex items-center gap-1 shrink-0"
                data-testid="rescreen-btn">
          <RefreshCw className={`w-3 h-3 ${rescreening ? 'animate-spin' : ''}`}/> Re-screen
        </button>
      </div>
    </div>
  );
}

function SeverityChip({ sev }) {
  const styles = {
    high:   'bg-[#B83A3A] text-white',
    medium: 'bg-[#FFB000] text-[#5A3A00]',
    low:    'bg-brand/15 text-brand',
  }[sev] || 'bg-surface-2 text-fg-muted';
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${styles} mt-0.5 shrink-0`}>{sev}</span>;
}

function escapeHtml(s) {  return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}

function renderBriefingHtml(data) {
  const { patient = {}, upcoming_appointment, interview = {}, ai_screen, treatment_plan = {}, recent_visits = [], generated_at } = data || {};
  const upDate = upcoming_appointment?.starts_at ? new Date(upcoming_appointment.starts_at).toLocaleString() : '—';
  const chips = [];
  if (upcoming_appointment?.is_first_visit) chips.push('<span class="chip chip-blue">FIRST VISIT</span>');
  if (upcoming_appointment?.new_condition) chips.push('<span class="chip chip-amber">NEW CONDITION</span>');
  if (upcoming_appointment?.status === 'pending_approval') chips.push('<span class="chip chip-amber">PENDING APPROVAL</span>');

  const flags = (ai_screen?.flags || []).map(f => `
    <li class="flag-${escapeHtml(f.severity)}">
      <span class="sev">${escapeHtml((f.severity||'').toUpperCase())}</span>
      <strong>${escapeHtml(f.tag)}</strong> — ${escapeHtml(f.why)}
    </li>`).join('');

  const visitsHtml = recent_visits.length === 0
    ? '<p class="muted">No prior visits.</p>'
    : recent_visits.map(v => `
        <div class="visit">
          <div class="visit-h"><strong>${escapeHtml(v.visit_date)}</strong>${v.therapist_name ? ` · ${escapeHtml(v.therapist_name)}` : ''}</div>
          ${v.subjective ? `<div><b>S:</b> ${escapeHtml(v.subjective)}</div>` : ''}
          ${v.objective ? `<div><b>O:</b> ${escapeHtml(v.objective)}</div>` : ''}
          ${v.assessment ? `<div><b>A:</b> ${escapeHtml(v.assessment)}</div>` : ''}
          ${v.plan ? `<div><b>P:</b> ${escapeHtml(v.plan)}</div>` : ''}
        </div>`).join('');

  return `
    <style>
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1a2030;line-height:1.45;padding:0;margin:0}
      h1{font-size:22px;margin:0 0 4px;color:#003D80}
      h2{font-size:13px;text-transform:uppercase;letter-spacing:.08em;color:#003D80;margin:18px 0 6px;border-bottom:1px solid #D5DFEC;padding-bottom:3px}
      .muted{color:#6c7484;font-size:12px}
      .meta{font-size:12px;color:#4a5366;margin-bottom:14px}
      .upcoming{background:#FFF4D6;border:1px solid #FFB000;border-radius:8px;padding:10px 12px;margin-bottom:12px}
      .upcoming.calm{background:#EEF3FB;border-color:#A6BBD6}
      .chip{display:inline-block;font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;margin-left:6px;letter-spacing:.05em}
      .chip-blue{background:#003D80;color:white}
      .chip-amber{background:#FFB000;color:#5A3A00}
      .reason{font-style:italic;color:#3a4253;margin-top:6px}
      ul.flags{list-style:none;padding:0;margin:0}
      ul.flags li{padding:5px 7px;border-radius:5px;margin-bottom:4px;font-size:12px}
      ul.flags .flag-high{background:#FBE9E5;border-left:3px solid #B83A3A}
      ul.flags .flag-medium{background:#FFF4D6;border-left:3px solid #FFB000}
      ul.flags .flag-low{background:#EEF3FB;border-left:3px solid #003D80}
      ul.flags .sev{font-weight:800;font-size:10px;letter-spacing:.06em;margin-right:6px;color:#3a4253}
      .visit{padding:6px 8px;border-left:2px solid #D5DFEC;margin-bottom:6px;font-size:12px}
      .visit-h{margin-bottom:3px}
      .footer{margin-top:24px;font-size:10px;color:#8c93a3;text-align:right;border-top:1px solid #eee;padding-top:6px}
      .grid2{display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px}
    </style>
    <h1>Pre-visit briefing — ${escapeHtml(patient.full_name || '')}</h1>
    <div class="meta">
      ${escapeHtml(patient.patient_code || '')} · DOB ${escapeHtml(patient.date_of_birth || '—')}
      · ${escapeHtml(patient.country || 'PL')}
      ${patient.email ? ' · ' + escapeHtml(patient.email) : ''}
    </div>

    <div class="upcoming ${(upcoming_appointment?.is_first_visit || upcoming_appointment?.new_condition) ? '' : 'calm'}">
      <div><b>Next visit:</b> ${escapeHtml(upDate)}${chips.join('')}</div>
      ${upcoming_appointment?.reason_for_visit
        ? `<div class="reason">"${escapeHtml(upcoming_appointment.reason_for_visit)}"</div>`
        : '<div class="muted" style="margin-top:4px">No patient-stated reason.</div>'}
    </div>

    <h2>AI red-flag screen</h2>
    ${ai_screen
      ? (flags ? `<ul class="flags">${flags}</ul>` : '<p class="muted">No red flags identified.</p>')
      : '<p class="muted">No AI screen recorded — intake form may be incomplete.</p>'}

    <h2>Patient summary</h2>
    <div class="grid2">
      <div><b>Chief complaint:</b> ${escapeHtml(interview.chief_complaint || '—')}</div>
      <div><b>Pain (0-10):</b> ${escapeHtml(interview.pain_score != null ? String(interview.pain_score) : '—')}</div>
      <div><b>Onset:</b> ${escapeHtml(interview.onset || '—')}</div>
      <div><b>Goals:</b> ${escapeHtml(interview.goals || '—')}</div>
      <div><b>Medications:</b> ${escapeHtml(interview.medications || '—')}</div>
      <div><b>Allergies:</b> ${escapeHtml(interview.allergies_list || '—')}</div>
    </div>

    <h2>Active treatment plan</h2>
    ${treatment_plan?.diagnosis || treatment_plan?.short_term_goals
      ? `<div class="grid2">
          <div><b>Diagnosis:</b> ${escapeHtml(treatment_plan.diagnosis || '—')}</div>
          <div><b>Frequency:</b> ${escapeHtml(treatment_plan.frequency || '—')}</div>
          <div><b>Short-term goals:</b> ${escapeHtml(treatment_plan.short_term_goals || '—')}</div>
          <div><b>Long-term goals:</b> ${escapeHtml(treatment_plan.long_term_goals || '—')}</div>
          <div><b>Modalities:</b> ${escapeHtml(treatment_plan.modalities || '—')}</div>
          <div><b>Duration:</b> ${escapeHtml(treatment_plan.duration_weeks ? treatment_plan.duration_weeks + ' weeks' : '—')}</div>
          ${treatment_plan.precautions ? `<div style="grid-column:1/3"><b>Precautions:</b> ${escapeHtml(treatment_plan.precautions)}</div>` : ''}
        </div>`
      : '<p class="muted">No active treatment plan.</p>'}

    <h2>Last ${recent_visits.length || 3} visits</h2>
    ${visitsHtml}

    <div class="footer">Generated ${new Date(generated_at || Date.now()).toLocaleString()} · Physiogenic — clinical use only, not for distribution.</div>
  `;
}
